import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Send, Eye, Edit, FileText, Shield, Clock, Sparkles, Loader2, Wand2, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminLegalEditor = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeDoc, setActiveDoc] = useState('terms');
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  const [addSectionText, setAddSectionText] = useState('');

  const [termsData, setTermsData] = useState({ content: '', version: '1.0', is_published: false, published_at: null, updated_at: null });
  const [privacyData, setPrivacyData] = useState({ content: '', version: '1.0', is_published: false, published_at: null, updated_at: null });

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    fetchDocuments();
  }, [isAdmin, navigate, token]);

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API}/api/admin/legal-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.terms) setTermsData(data.terms);
        if (data.privacy) setPrivacyData(data.privacy);
      }
    } catch (err) {
      toast.error('Failed to load documents');
    }
  };

  const currentData = activeDoc === 'terms' ? termsData : privacyData;
  const setCurrentData = activeDoc === 'terms' ? setTermsData : setPrivacyData;

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/legal-documents/${activeDoc}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: currentData.content, version: currentData.version, publish: false })
      });
      if (res.ok) {
        const updated = await res.json();
        setCurrentData(updated);
        toast.success('Draft saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!window.confirm(`Publish ${activeDoc === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'} v${currentData.version}?\n\nAll users on older versions will be prompted to re-accept on their next login.`)) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API}/api/admin/legal-documents/${activeDoc}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: currentData.content, version: currentData.version, publish: true })
      });
      if (res.ok) {
        const updated = await res.json();
        setCurrentData(updated);
        toast.success(`Published v${updated.version} — users will be prompted to re-accept`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Publish failed');
      }
    } catch { toast.error('Publish failed'); }
    finally { setPublishing(false); }
  };

  const bumpVersion = (type) => {
    const parts = currentData.version.split('.').map(Number);
    if (type === 'major') { parts[0]++; parts[1] = 0; }
    else { parts[1]++; }
    setCurrentData(prev => ({ ...prev, version: parts.join('.') }));
  };

  const handleAiAction = async (action, instruction = '') => {
    setAiLoading(true);
    setShowAiMenu(false);
    setPreviewMode(false);
    toast.info(`AI is ${action === 'review' ? 'reviewing' : action === 'enhance' ? 'enhancing' : action === 'generate' ? 'generating' : 'updating'} your document...`);
    try {
      const res = await fetch(`${API}/api/admin/legal-documents/ai-review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: currentData.content, doc_type: activeDoc, action, instruction })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'AI request failed');
      }
      const data = await res.json();
      if (data.content) {
        setCurrentData(prev => ({ ...prev, content: data.content }));
        toast.success('AI update complete — review the changes before publishing');
      }
    } catch (err) {
      toast.error(err.message || 'AI review failed');
    } finally {
      setAiLoading(false);
      setAddSectionText('');
    }
  };

  return (
    <div className="honeycomb-bg p-4">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2" data-testid="legal-editor-title">
              <Shield className="h-6 w-6 text-amber-600" />
              Legal Document Editor
            </h1>
          </div>
        </div>

        {/* Document Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => { setActiveDoc('terms'); setPreviewMode(false); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeDoc === 'terms' ? 'bg-amber-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
            data-testid="terms-tab"
          >
            <FileText className="w-4 h-4" /> Terms & Conditions
          </button>
          <button
            onClick={() => { setActiveDoc('privacy'); setPreviewMode(false); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeDoc === 'privacy' ? 'bg-amber-600 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
            data-testid="privacy-tab"
          >
            <Shield className="w-4 h-4" /> Privacy Policy
          </button>
        </div>

        {/* Status Bar */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-stone-500">Version:</span>
              <span className="font-bold text-stone-800 text-lg">{currentData.version}</span>
              <button onClick={() => bumpVersion('minor')} className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200" title="Minor version bump">+0.1</button>
              <button onClick={() => bumpVersion('major')} className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200" title="Major version bump">+1.0</button>
            </div>
            <div>
              {currentData.is_published ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Published</span>
              ) : (
                <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded-full font-medium">Draft</span>
              )}
            </div>
            {currentData.published_at && (
              <div className="flex items-center gap-1 text-xs text-stone-400">
                <Clock className="w-3 h-3" />
                Last published: {new Date(currentData.published_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewMode(!previewMode)}
              className={previewMode ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}
            >
              {previewMode ? <><Edit className="w-4 h-4 mr-1" /> Edit</> : <><Eye className="w-4 h-4 mr-1" /> Preview</>}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={saving}>
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button size="sm" onClick={handlePublish} disabled={publishing} className="bg-green-600 hover:bg-green-700 text-white">
              <Send className="w-4 h-4 mr-1" /> {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Version Input */}
        <div className="bg-white rounded-xl border border-stone-200 p-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-stone-500 mb-1">Version Number</label>
              <Input
                value={currentData.version}
                onChange={(e) => setCurrentData(prev => ({ ...prev, version: e.target.value }))}
                placeholder="e.g. 2.0"
                className="h-9"
                data-testid="version-input"
              />
            </div>
            <div className="flex-[3]">
              <label className="block text-xs font-medium text-stone-500 mb-1">Format Guide</label>
              <p className="text-xs text-stone-400">Use Markdown: **bold**, *italic*, ## Heading, - bullet points, [link](url)</p>
            </div>
          </div>
        </div>

        {/* AI Toolbar */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 mb-4" data-testid="ai-toolbar">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-800 text-sm">AI Legal Assistant</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline" size="sm" disabled={aiLoading}
                onClick={() => handleAiAction('review')}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="ai-review-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Review & Improve
              </Button>
              <Button
                variant="outline" size="sm" disabled={aiLoading}
                onClick={() => handleAiAction('enhance')}
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="ai-enhance-btn"
              >
                <Wand2 className="w-4 h-4 mr-1" /> Strengthen Language
              </Button>
              <Button
                variant="outline" size="sm" disabled={aiLoading || !currentData.content}
                onClick={() => setShowAiMenu(!showAiMenu)}
                className="border-green-300 text-green-700 hover:bg-green-100"
                data-testid="ai-add-section-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Section
              </Button>
              <Button
                variant="outline" size="sm" disabled={aiLoading}
                onClick={() => handleAiAction('generate')}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                data-testid="ai-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Generate Full Document
              </Button>
            </div>
          </div>
          {showAiMenu && (
            <div className="mt-3 flex items-center gap-2">
              <Input
                value={addSectionText}
                onChange={(e) => setAddSectionText(e.target.value)}
                placeholder="Describe the section to add, e.g. 'Data breach notification policy' or 'Refund terms for annual subscriptions'"
                className="flex-1 h-9 bg-white text-sm"
                onKeyDown={(e) => e.key === 'Enter' && addSectionText.trim() && handleAiAction('add_section', addSectionText)}
              />
              <Button size="sm" disabled={!addSectionText.trim() || aiLoading}
                onClick={() => handleAiAction('add_section', addSectionText)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </Button>
            </div>
          )}
          {aiLoading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-purple-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              AI is working... content will stream in below
            </div>
          )}
        </div>

        {/* Editor / Preview */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {previewMode ? (
            <div className="p-6 prose prose-stone max-w-none min-h-[500px]">
              <ReactMarkdown>{currentData.content || '*No content yet. Switch to Edit mode to start writing.*'}</ReactMarkdown>
            </div>
          ) : (
            <Textarea
              value={currentData.content}
              onChange={(e) => setCurrentData(prev => ({ ...prev, content: e.target.value }))}
              placeholder={`Write your ${activeDoc === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'} here using Markdown...\n\n## 1. Introduction\n\nWelcome to Thrive...`}
              className="min-h-[500px] border-0 rounded-none p-6 text-sm font-mono resize-y focus-visible:ring-0"
              data-testid="content-editor"
            />
          )}
        </div>

        {/* Help */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1 text-amber-700">
            <li><strong>Save Draft</strong> — saves your changes without affecting what users see</li>
            <li><strong>Publish</strong> — makes this version live; users on older versions will be prompted to re-accept on next login</li>
            <li><strong>Version bump</strong> — use +0.1 for minor edits, +1.0 for significant changes that require re-acceptance</li>
            <li>The acceptance dialog fetches the published content automatically</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminLegalEditor;
